// @services/stubs/StubInviteAcceptClient.ts
import * as E from "fp-ts/lib/Either";
import type { InviteAcceptClient } from "@services/clients/InviteAcceptClient";
import type { InviteAcceptRequest } from "@ps-bridge/controllers/inviteAccept";
import type { LoadingErrorInput } from "@ps-bridge/controllers/loading";
import type { OptionTs } from "@ps-bridge/interop";
import type { TaskEitherTs } from "@ps-bridge/interop";
import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import { toLoadingErrorInput } from "@utils/loadingError";
import { taskEitherFromRaw } from "@utils/effects/branded";

const delay = (ms: number): Promise<Ok> =>
    new Promise((resolve) => setTimeout(() => resolve(ok), ms));

const STUB_INVITE_ACCEPT_MODE_KEY = "stub:inviteAccept:mode";
const STUB_INVITE_ACCEPT_ERROR_MESSAGE_KEY = "stub:inviteAccept:errorMessage";
const DEFAULT_INVITE_ACCEPT_ERROR_MESSAGE = "Invite acceptance failed.";

const getStubStorageValue = (key: string): OptionTs<string> => {
    if (typeof window === "undefined") {
        return Option.none();
    }
    try {
        const value = window.localStorage.getItem(key);
        return typeof value === "string" ? Option.some(value) : Option.none();
    } catch {
        return Option.none();
    }
};

const getStubInviteAcceptMode = (): "success" | "error" =>
    Option.fold(
        getStubStorageValue(STUB_INVITE_ACCEPT_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildInviteAcceptError = (): LoadingErrorInput =>
    toLoadingErrorInput(
        Option.fold(
            getStubStorageValue(STUB_INVITE_ACCEPT_ERROR_MESSAGE_KEY),
            () => DEFAULT_INVITE_ACCEPT_ERROR_MESSAGE,
            (value) => value,
        ),
        Option.none(),
    );

export class StubInviteAcceptClient implements InviteAcceptClient {
    acceptInvite(_request: InviteAcceptRequest): TaskEitherTs<LoadingErrorInput, Ok> {
        return taskEitherFromRaw(async () => {
            await delay(100);
            if (getStubInviteAcceptMode() === "error") {
                return E.left(buildInviteAcceptError());
            }
            return E.right(ok);
        });
    }
}
